import React, { useState } from 'react';
import { User, Loader2, AlertCircle, Mail } from 'lucide-react';
import Modal from '../ui/Modal';
import { supabase } from '../../lib/supabase';

interface GardenLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  plantName: string;
}

export default function GardenLoginModal({ isOpen, onClose, plantName }: GardenLoginModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Send magic link
      const { error: magicLinkError } = await supabase.auth.signInWithOtp({
        email: email.toLowerCase(),
        options: {
          emailRedirectTo: `${window.location.origin}/magic-link-callback`,
        }
      });

      if (magicLinkError) throw magicLinkError;
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-primary-100 rounded-full">
            <User className="h-8 w-8 text-primary-500" />
          </div>
        </div>
        <h3 className="text-xl font-semibold mb-2">
          Add {plantName} to Your Garden
        </h3>
        
        {success ? (
          <div className="space-y-4">
            <p className="text-green-600 font-medium">Magic link sent!</p>
            <p className="text-gray-600">
              We've sent a magic link to your email. Click the link to sign in and add {plantName} to your garden.
            </p>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-6">
              Enter your email to sign in with a magic link and add plants to your garden.
            </p>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type="email"
                  required
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary-500 text-white py-2 px-4 rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  'Send Magic Link'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
}